import { useCallback, useState } from "react";
import { api } from "../../utils/api";
import {
  DirectReplyRequest,
  DirectReplyResponse,
  DirectReplyUpdateRequest,
  DirectReplyDeleteRequest,
  OdoDetailResponse,
  OdoDetailItem,
  OdoItem,
  OdoListResponse,
} from "../../types/odo";

interface OdoState {
  odoList: OdoItem[];
  totalCount: number;
  currentPage: number;
  odoDetail: OdoDetailItem | null;
}

export const useServiceOdo = () => {
  const [state, setState] = useState<OdoState>({
    odoList: [],
    totalCount: 0,
    currentPage: 1,
    odoDetail: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isReplyLoading, setIsReplyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);

  const handleError = (error: any) => {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "알 수 없는 오류가 발생했습니다.";

    setError(errorMessage);
  };

  const handleReplyError = (error: any) => {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "답변 처리 중 오류가 발생했습니다.";

    setReplyError(errorMessage);
  };

  const handleDetailError = (error: any) => {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "상세 정보 처리 중 오류가 발생했습니다.";

    setDetailError(errorMessage);
  };

  const fetchOdoList = useCallback(
    async (page: number = 1, process_type: string, service_type: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.get<OdoListResponse>(
          `/admin/v1/odo/list?page=${page}&process_type=${process_type}&service_type=${service_type}`
        );

        if (response.data.status) {
          setState((prev) => ({
            ...prev,
            odoList: response.data.data,
            totalCount: response.data.total_count,
            currentPage: page,
          }));
        } else {
          throw new Error("API 응답 상태가 실패입니다.");
        }
      } catch (error) {
        handleError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const fetchOdoSearch = useCallback(async (search: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<OdoListResponse>(
        `/admin/v1/odo/search?query=${encodeURIComponent(search)}`
      );

      if (response.data.status) {
        setState((prev) => ({
          ...prev,
          odoList: response.data.data,
          totalCount: response.data.total_count,
        }));
      } else {
        throw new Error("검색 결과를 가져올 수 없습니다.");
      }
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchOdoDetail = useCallback(async (serviceId: string) => {
    setIsDetailLoading(true);
    setDetailError(null);

    try {
      const response = await api.get<OdoDetailResponse>(
        `/admin/v1/odo/detail?service_id=${serviceId}`
      );

      if (response.data.status) {
        setState((prev) => ({
          ...prev,
          odoDetail: response.data.data,
        }));
      } else {
        throw new Error("상세 정보를 가져올 수 없습니다.");
      }
    } catch (error) {
      handleDetailError(error);
      return false;
    } finally {
      setIsDetailLoading(false);
    }
  }, []);

  const DirectReplyCreate = useCallback(
    async (replyData: DirectReplyRequest) => {
      setIsReplyLoading(true);
      setReplyError(null);

      try {
        const response = await api.post<DirectReplyResponse>(
          `/admin/v1/direct/reply/create`,
          replyData
        );

        if (response.data.status) {
          return true;
        } else {
          throw new Error("답변 생성에 실패했습니다.");
        }
      } catch (error) {
        handleReplyError(error);
        return false;
      } finally {
        setIsReplyLoading(false);
      }
    },
    []
  );

  const DirectReplyUpdate = useCallback(
    async (replyData: DirectReplyUpdateRequest) => {
      setIsReplyLoading(true);
      setReplyError(null);

      try {
        const response = await api.post<DirectReplyResponse>(
          `/admin/v1/direct/reply/update`,
          replyData
        );

        if (response.data.status) {
          return true;
        } else {
          throw new Error("답변 수정에 실패했습니다.");
        }
      } catch (error) {
        handleReplyError(error);
        return false;
      } finally {
        setIsReplyLoading(false);
      }
    },
    []
  );

  const DirectReplyDelete = useCallback(
    async (deleteData: DirectReplyDeleteRequest) => {
      setIsReplyLoading(true);
      setReplyError(null);

      try {
        const response = await api.post<DirectReplyResponse>(
          `/admin/v1/direct/reply/delete`,
          deleteData
        );

        if (response.data.status) {
          return true;
        } else {
          throw new Error("답변 삭제에 실패했습니다.");
        }
      } catch (error) {
        handleReplyError(error);
        return false;
      } finally {
        setIsReplyLoading(false);
      }
    },
    []
  );

  const resetState = useCallback(() => {
    setState({
      odoList: [],
      totalCount: 0,
      currentPage: 1,
      odoDetail: null,
    });
    setError(null);
    setReplyError(null);
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    setState((prev) => ({
      ...prev,
      currentPage: page,
    }));
  }, []);

  return {
    odoList: state.odoList,
    totalCount: state.totalCount,
    currentPage: state.currentPage,
    odoDetail: state.odoDetail,

    isLoading,
    isDetailLoading,
    isReplyLoading,
    error,
    replyError,
    detailError,

    fetchOdoList,
    fetchOdoSearch,
    fetchOdoDetail,
    DirectReplyCreate,
    DirectReplyUpdate,
    DirectReplyDelete,

    resetState,
    setCurrentPage,

    totalPages: Math.ceil(state.totalCount / 10),
    hasOdos: state.odoList.length > 0,
    isEmpty: state.odoList.length === 0 && !isLoading,
  };
};
